/**
 * WhatsApp Message Templates
 * List, sync, and delete WhatsApp Business message templates via Meta.
 * Creating a new template is a dedicated full-page experience (WhatsAppTemplateNew.tsx).
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button,
  IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, CircularProgress, Stack, Tooltip,
  TextField, Divider, Checkbox, FormControlLabel, TablePagination, MenuItem, Select,
  InputAdornment, Grid,
} from '@mui/material';
import {
  Add, Sync, InfoOutlined, EditOutlined, SendOutlined, Search,
  DeleteOutline, LocalOfferOutlined, VisibilityOutlined, UpdateRounded,
  CheckCircleRounded, ScheduleRounded, ErrorOutlineRounded, PauseCircleOutlineRounded,
  BlockRounded, DescriptionOutlined, TextSnippetOutlined, ImageOutlined, VideocamOutlined,
  InsertDriveFileOutlined, ViewCarouselOutlined, FilterListRounded, CloseRounded,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import adminApi, { WhatsAppTemplate, WhatsAppTemplateStatusEvent } from '@/services/admin.api';
import WhatsAppPreviewBubble from './campaigns/WhatsAppPreviewBubble';
import { usePlanGate } from '@/hooks/usePlanGate';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { isTemplateMediaUnfit } from '@/utils/whatsapp-template-media.util';
import { field, useUrlListState } from '@/hooks/admin/useUrlListState';
import WhatsAppSectionTabs from './whatsapp/WhatsAppSectionTabs';

/** Human-readable label for a `statusHistory` entry's `source`. */
function getSourceLabel(source: WhatsAppTemplateStatusEvent['source']): string {
  switch (source) {
    case 'draft':
      return 'Saved as draft';
    case 'submit':
      return 'Submitted to Meta';
    case 'sync':
      return 'Sync from Meta';
    case 'webhook':
      return 'Meta status update';
    case 'disabled-sweep':
      return 'Removed from connected account';
    default:
      return source;
  }
}

/** Substitute `{{n}}` placeholders with Meta's stored sample values, for preview. */
function renderPreviewText(text: string, examples?: string[]): string {
  return (text || '').replace(/\{\{(\d+)\}\}/g, (match, idx) => {
    const value = examples?.[parseInt(idx, 10) - 1];
    return value || match;
  });
}

/** Returns the count of distinct `{{n}}` placeholders in a template body. */
function countBodyVariables(text: string): number {
  const found = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || ''))) {
    found.add(parseInt(m[1] || '0', 10));
  }
  return found.size;
}

function getStatusColor(status: WhatsAppTemplate['status']) {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
    case 'DISABLED':
      return 'error';
    case 'PAUSED':
      return 'warning';
    case 'PENDING':
      return 'info';
    default:
      return 'default';
  }
}

/** Title-case a status value for display, e.g. "APPROVED" -> "Approved". */
function formatStatusLabel(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

/** Subtle dot + text status indicator, in the style of Meta Business Manager. */
type StatusTab = 'ALL' | 'DRAFT' | 'PENDING' | 'APPROVED' | 'ACTION';

const STATUS_TABS: Array<{ value: StatusTab; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'ACTION', label: 'Action Required' },
];

/** Statuses that need a human to do something: rejected (fix + resubmit),
 *  paused (quality issue), disabled (gone from the connected account). An
 *  APPROVED-but-media-unfit template also needs action (upload media) — see
 *  `needsAction` below, which folds that case into the same "Action Required" tab. */
const ACTION_REQUIRED_STATUSES: Array<WhatsAppTemplate['status']> = ['REJECTED', 'PAUSED', 'DISABLED'];

function matchesStatusTab(status: WhatsAppTemplate['status'], tab: StatusTab): boolean {
  if (tab === 'ALL') return true;
  if (tab === 'ACTION') return ACTION_REQUIRED_STATUSES.includes(status);
  return status === tab;
}

/** What the tile's type chip says — the header media kind, CAROUSEL for card
 *  templates, TEXT for header-less / text-header templates. */
type HeaderKind = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'CAROUSEL';
function templateHeaderKind(t: WhatsAppTemplate): HeaderKind {
  if (t.components?.carousel?.cards?.length) return 'CAROUSEL';
  const type = t.components?.header?.type;
  if (type === 'IMAGE' || type === 'VIDEO' || type === 'DOCUMENT') return type;
  return 'TEXT';
}
/** Top-right type indicator — deliberately monochrome; only the icon and
 *  label change per kind, so it reads as metadata rather than competing
 *  with the status badge for attention. */
const HEADER_KIND_META: Record<HeaderKind, { Icon: React.ElementType; label: string }> = {
  TEXT: { Icon: TextSnippetOutlined, label: 'Text' },
  IMAGE: { Icon: ImageOutlined, label: 'Image' },
  VIDEO: { Icon: VideocamOutlined, label: 'Video' },
  DOCUMENT: { Icon: InsertDriveFileOutlined, label: 'Document' },
  CAROUSEL: { Icon: ViewCarouselOutlined, label: 'Carousel' },
};

function TypeBadge({ kind }: { kind: HeaderKind }) {
  const meta = HEADER_KIND_META[kind];
  const Icon = meta.Icon;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', flexShrink: 0 }}>
      <Icon sx={{ fontSize: 15 }} />
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}
      >
        {meta.label}
      </Typography>
    </Stack>
  );
}

/** Soft tinted badge for the card's status — icon + label on a muted
 *  background, in place of the old solid-fill MUI Chip. */
const STATUS_META: Record<WhatsAppTemplate['status'], { Icon: React.ElementType; bg: string; fg: string }> = {
  APPROVED: { Icon: CheckCircleRounded, bg: '#e6f6ec', fg: '#1e7e42' },
  PENDING: { Icon: ScheduleRounded, bg: '#e8f0fe', fg: '#1a56db' },
  DRAFT: { Icon: DescriptionOutlined, bg: '#f1f3f5', fg: '#5f6368' },
  REJECTED: { Icon: ErrorOutlineRounded, bg: '#fdecea', fg: '#c0392b' },
  PAUSED: { Icon: PauseCircleOutlineRounded, bg: '#fef3e2', fg: '#b7660b' },
  DISABLED: { Icon: BlockRounded, bg: '#f1f3f5', fg: '#5f6368' },
};

function StatusBadge({ status }: { status: WhatsAppTemplate['status'] }) {
  const meta = STATUS_META[status];
  const Icon = meta.Icon;
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ bgcolor: meta.bg, color: meta.fg, borderRadius: 1.5, px: 1, py: 0.375, flexShrink: 0 }}
    >
      <Icon sx={{ fontSize: 14 }} />
      <Typography variant="caption" fontWeight={600} lineHeight={1}>
        {formatStatusLabel(status)}
      </Typography>
    </Stack>
  );
}

/** Segmented pill filter bar for the status tabs — every tab carries the
 *  same weight (incl. Approved); only the active one is lifted, so nothing
 *  reads as more important than the rest of the queue. */
function StatusTabBar({
  value,
  counts,
  onChange,
}: {
  value: StatusTab;
  counts: Record<StatusTab, number>;
  onChange: (v: StatusTab) => void;
}) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
      {STATUS_TABS.map((tab) => {
        const active = tab.value === value;
        return (
          <Box
            key={tab.value}
            onClick={() => onChange(tab.value)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.625,
              borderRadius: 1.5,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: 13.5,
              fontWeight: 600,
              color: active ? 'text.primary' : 'text.secondary',
              bgcolor: active ? 'background.paper' : 'transparent',
              boxShadow: active ? '0 1px 3px rgba(15,23,42,0.1)' : 'none',
              transition: 'background-color .15s, color .15s, box-shadow .15s',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <span>{tab.label}</span>
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                minWidth: 18,
                textAlign: 'center',
                px: 0.5,
                borderRadius: 5,
                lineHeight: '16px',
                bgcolor: active ? 'primary.main' : 'grey.300',
                color: active ? '#fff' : 'text.secondary',
              }}
            >
              {counts[tab.value]}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function StatusIndicator({ status }: { status: WhatsAppTemplate['status'] }) {
  const color = getStatusColor(status);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: color === 'default' ? 'grey.400' : `${color}.main`,
        }}
      />
      <Typography variant="body2" color="text.secondary">
        {formatStatusLabel(status)}
      </Typography>
    </Stack>
  );
}

/** Filtering here is client-side over an already-loaded set, but the view is
 *  still worth a link ("the templates that need action"), so it lives in the URL
 *  like every other list. */
const TEMPLATES_URL_SPEC = {
  status: field.enumOf(['ALL', 'DRAFT', 'PENDING', 'APPROVED', 'ACTION'] as const, 'ALL'),
  search: field.str(),
  category: field.enumOf(['', 'MARKETING', 'UTILITY', 'AUTHENTICATION'] as const, ''),
  showDisabled: field.bool(false),
  page: field.pageIndex(),
};

const WhatsAppTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { guard } = usePlanGate();
  const location = useLocation();
  // Mounted under both /admin and / (see App.tsx) — navigate by absolute path
  // so the editor resolves the same from either tree.
  const pathPrefix = location.pathname.startsWith('/admin') ? '/admin' : '';
  const newTemplatePath = `${pathPrefix}/whatsapp-templates/new`;
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  /** `${name}::${language}` of the draft currently being submitted, for the spinner. */
  const [submittingDraft, setSubmittingDraft] = useState<string | null>(null);

  const [labelsTarget, setLabelsTarget] = useState<WhatsAppTemplate | null>(null);
  const [labelsValues, setLabelsValues] = useState<string[]>([]);
  const [headerLabelValue, setHeaderLabelValue] = useState('');
  const [labelsSaving, setLabelsSaving] = useState(false);

  const [infoTarget, setInfoTarget] = useState<WhatsAppTemplate | null>(null);
  // List filters + pagination — status tab (All / Draft / Pending / Approved /
  // Action Required), free-text name search, category, the disabled toggle and
  // page controls.
  const { state: query, patch: patchQuery } = useUrlListState(TEMPLATES_URL_SPEC);
  const showDisabled = query.showDisabled;
  const setShowDisabled = (value: boolean) => patchQuery({ showDisabled: value });
  const statusTab = query.status;
  const setStatusTab = (value: StatusTab) => patchQuery({ status: value });
  // The input keeps local state so typing is instant; only the debounced value
  // reaches the query. Binding it straight to the URL meant a router navigation
  // and a re-render on every keystroke.
  const [searchInput, setSearchInput] = useState(query.search);
  const search = query.search;
  const setSearch = setSearchInput;
  const categoryFilter = query.category;
  const setCategoryFilter = (value: '' | 'MARKETING' | 'UTILITY' | 'AUTHENTICATION') =>
    patchQuery({ category: value });
  const page = query.page;
  const setPage = (value: number) => patchQuery({ page: value });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [previewTarget, setPreviewTarget] = useState<WhatsAppTemplate | null>(null);
  const [previewMedia, setPreviewMedia] = useState<{ previewUrl: string | null; fileName?: string } | null>(null);
  const [previewMediaLoading, setPreviewMediaLoading] = useState(false);
  const [previewCarouselMedia, setPreviewCarouselMedia] = useState<
    Array<{ previewUrl: string | null; fileName?: string }>
  >([]);
  const [previewCarouselMediaLoading, setPreviewCarouselMediaLoading] = useState(false);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getWhatsAppTemplates();
      setTemplates(data);
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || err.message || 'Failed to load templates'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // Commit the search box to the query after a pause. A no-op patch — this
  // firing on mount with the value it just read out of the URL — is dropped
  // inside the hook.
  useEffect(() => {
    const timer = setTimeout(() => patchQuery({ search: searchInput.trim() }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, patchQuery]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError('');
      const result = await adminApi.syncWhatsAppTemplates();
      setTemplates(result.templates);
      setSuccess(result.message || 'Synced templates from Meta');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(
          err.response?.data?.error?.message || err.message || 'Failed to sync templates'
        );
      }
    } finally {
      setSyncing(false);
    }
  };

  /** Open the live preview dialog, fetching a signed URL for any uploaded header media
   *  (or, for a CAROUSEL template, one signed URL per card). */
  const openPreview = async (t: WhatsAppTemplate) => {
    setPreviewTarget(t);
    setPreviewMedia(null);
    setPreviewCarouselMedia([]);

    const cards = t.components?.carousel?.cards;
    if (cards && cards.length > 0) {
      setPreviewCarouselMediaLoading(true);
      try {
        const media = await adminApi.getWhatsAppTemplateCarouselMediaPreview(t.name, t.language);
        setPreviewCarouselMedia(media);
      } catch {
        setPreviewCarouselMedia([]);
      } finally {
        setPreviewCarouselMediaLoading(false);
      }
      return;
    }

    const header = t.components?.header;
    const isMediaHeader = header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type);
    if (!isMediaHeader || !(header.headerMediaS3Key || header.headerMediaId)) return;

    setPreviewMediaLoading(true);
    try {
      const media = await adminApi.getWhatsAppTemplateHeaderMediaPreview(t.name, t.language);
      setPreviewMedia(media);
    } catch {
      setPreviewMedia(null);
    } finally {
      setPreviewMediaLoading(false);
    }
  };

  const handleSaveVariableLabels = async () => {
    if (!labelsTarget) return;
    try {
      setLabelsSaving(true);
      setError('');
      const headerHasTextVar = labelsTarget.components?.header?.type === 'TEXT'
        && /\{\{\s*1\s*\}\}/.test(labelsTarget.components.header.text || '');
      const updated = await adminApi.updateWhatsAppTemplateVariableLabels(
        labelsTarget.name,
        labelsTarget.language,
        {
          variableLabels: labelsValues.map((v) => v.trim()),
          ...(headerHasTextVar ? { headerVariableLabel: headerLabelValue.trim() } : {}),
        }
      );
      setTemplates((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
      setLabelsTarget(null);
      setSuccess('Variable labels saved');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(
          err.response?.data?.error?.message || err.message || 'Failed to save variable labels'
        );
      }
    } finally {
      setLabelsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setError('');
      await adminApi.deleteWhatsAppTemplate(deleteTarget);
      setDeleteTarget(null);
      await loadTemplates();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(
          err.response?.data?.error?.message || err.message || 'Failed to delete template'
        );
      }
    }
  };

  const handleSubmitDraft = async (t: { name: string; language: string }) => {
    const key = `${t.name}::${t.language}`;
    try {
      setError('');
      setSubmittingDraft(key);
      await adminApi.submitWhatsAppTemplateDraft(t.name, t.language);
      await loadTemplates();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(
          err.response?.data?.error?.message || err.message || 'Failed to submit draft to Meta'
        );
      }
    } finally {
      setSubmittingDraft(null);
    }
  };

  // Base set honors the "show unavailable" toggle for the All tab; the Action
  // Required tab always includes DISABLED (that's exactly where you'd look).
  const baseTemplates = showDisabled ? templates : templates.filter((t) => t.status !== 'DISABLED');
  // APPROVED on Meta but unsendable here (media expired or never uploaded). Scoped
  // to APPROVED so drafts-in-progress aren't nagged. Drives the per-card badge/CTA.
  const isUnfitRow = (t: WhatsAppTemplate) => t.status === 'APPROVED' && isTemplateMediaUnfit(t);
  // "Action Required" = a Meta status that needs a human (rejected/paused/disabled)
  // OR an approved template that can't send because its media is missing. One tab,
  // one place to look for "templates I need to do something about".
  const needsAction = (t: WhatsAppTemplate) =>
    ACTION_REQUIRED_STATUSES.includes(t.status) || isUnfitRow(t);
  const tabCounts: Record<StatusTab, number> = {
    ALL: baseTemplates.length,
    DRAFT: baseTemplates.filter((t) => t.status === 'DRAFT').length,
    PENDING: baseTemplates.filter((t) => t.status === 'PENDING').length,
    APPROVED: baseTemplates.filter((t) => t.status === 'APPROVED').length,
    ACTION: templates.filter(needsAction).length,
  };
  const filteredTemplates = (statusTab === 'ACTION' ? templates : baseTemplates)
    .filter((t) => (statusTab === 'ACTION' ? needsAction(t) : matchesStatusTab(t.status, statusTab)))
    .filter((t) => (categoryFilter ? t.category === categoryFilter : true))
    .filter((t) => (search.trim() ? t.name.toLowerCase().includes(search.trim().toLowerCase()) : true));
  const visibleTemplates = filteredTemplates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const hasActiveFilter = statusTab !== 'ALL' || !!search.trim() || !!categoryFilter;

  // Returning to page 1 on a filter change is handled by the hook, which knows
  // which spec field is the page.

  return (
    <Box>
      <WhatsAppSectionTabs active="templates" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight="bold">
          WhatsApp Templates
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
              />
            }
            label={<Typography variant="body2" color="text.secondary">Show unavailable templates</Typography>}
          />
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <Sync />}
            onClick={guard('whatsappEnabled', handleSync)}
            disabled={syncing || loading}
          >
            Sync from Meta
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={guard('whatsappEnabled', () => navigate(newTemplatePath))}>
            New Template
          </Button>
        </Stack>
      </Box>

      {/* Unified filter toolbar — status queue on the left, search + category on the
          right, all at equal visual weight so no single control (e.g. Approved) reads
          as more important than the rest. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <StatusTabBar value={statusTab} counts={tabCounts} onChange={setStatusTab} />

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search templates"
            value={searchInput}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: 210,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'grey.100',
                '& fieldset': { border: 'none' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Select
              size="small"
              displayEmpty
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as typeof categoryFilter)}
              renderValue={(v) => (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <FilterListRounded fontSize="small" />
                  <span>{v ? v.charAt(0) + v.slice(1).toLowerCase() : 'Category'}</span>
                </Stack>
              )}
              sx={{
                minWidth: 150,
                borderRadius: 2,
                fontSize: 13.5,
                fontWeight: 600,
                bgcolor: categoryFilter ? 'rgba(79,70,229,0.08)' : 'grey.100',
                color: categoryFilter ? 'primary.main' : 'text.secondary',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: categoryFilter ? '1px solid rgba(79,70,229,0.35)' : 'none',
                },
                '& .MuiSelect-select': { py: 0.875 },
              }}
            >
              <MenuItem value="">All categories</MenuItem>
              <MenuItem value="MARKETING">Marketing</MenuItem>
              <MenuItem value="UTILITY">Utility</MenuItem>
              <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
            </Select>
            {categoryFilter && (
              <Tooltip title="Clear category">
                <IconButton size="small" onClick={() => setCategoryFilter('')}>
                  <CloseRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {hasActiveFilter && (
            <Button
              size="small"
              onClick={() => {
                setStatusTab('ALL');
                setSearch('');
                setCategoryFilter('');
              }}
              sx={{ color: 'text.secondary', fontWeight: 500 }}
            >
              Clear filters
            </Button>
          )}
        </Stack>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : filteredTemplates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              {templates.length === 0
                ? 'No templates yet. Create one to get started, or sync existing templates from Meta.'
                : hasActiveFilter
                  ? 'No templates match the current tab / search / category.'
                  : 'No templates to show. Enable "Show unavailable templates" to see templates no longer on this account.'}
            </Typography>
          ) : (
            <Grid container spacing={2.5}>
              {visibleTemplates.map((t) => {
                const headerKind = templateHeaderKind(t);
                const bodyPreview = renderPreviewText(
                  t.components?.body?.text || '',
                  t.components?.body?.example?.body_text?.[0]
                );
                const hasVariables =
                  countBodyVariables(t.components?.body?.text || '') > 0 ||
                  (t.components?.header?.type === 'TEXT' &&
                    /\{\{\s*1\s*\}\}/.test(t.components.header.text || ''));
                return (
                  <Grid item xs={12} sm={6} md={4} key={t._id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        borderColor: 'divider',
                        transition: 'box-shadow .15s ease, border-color .15s ease',
                        '&:hover': { boxShadow: '0 4px 14px rgba(15,23,42,0.08)', borderColor: '#cbd5e1' },
                      }}
                    >
                      <CardContent sx={{ pb: 1, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5} spacing={1}>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            {/* When media is missing, the "needs re-upload" state is
                                the one that matters — show it INSTEAD of the (true but
                                misleading) Approved badge. */}
                            {!isUnfitRow(t) && <StatusBadge status={t.status} />}
                            {isUnfitRow(t) && (
                              <Tooltip
                                title={
                                  t.mediaHealthReason
                                    ? `Media unavailable: ${t.mediaHealthReason}. Upload media to resume sending — no Meta re-approval needed.`
                                    : 'This template’s media isn’t available. Upload media to resume sending — no Meta re-approval needed.'
                                }
                              >
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 1,
                                    bgcolor: '#fef3e2',
                                    color: '#b54708',
                                    border: '1px solid #f5c98a',
                                    fontSize: 11,
                                    fontWeight: 600,
                                  }}
                                >
                                  <ErrorOutlineRounded sx={{ fontSize: 13 }} />
                                  Media needs re-upload
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>
                          <TypeBadge kind={headerKind} />
                        </Stack>

                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          onClick={() => openPreview(t)}
                          sx={{
                            cursor: 'pointer',
                            wordBreak: 'break-word',
                            lineHeight: 1.3,
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {t.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                          {t.category} · {t.language}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            flexGrow: 1,
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {bodyPreview || <em>No body text</em>}
                        </Typography>
                        {t.status === 'REJECTED' && t.rejectedReason && (
                          <Typography
                            variant="caption"
                            color="error.main"
                            onClick={() => setInfoTarget(t)}
                            sx={{ cursor: 'pointer', mt: 1, '&:hover': { textDecoration: 'underline' } }}
                          >
                            Rejected — view details
                          </Typography>
                        )}
                        {t.status === 'DISABLED' && t.rejectedReason && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            onClick={() => setInfoTarget(t)}
                            sx={{ cursor: 'pointer', mt: 1, '&:hover': { textDecoration: 'underline' } }}
                          >
                            Unavailable — view details
                          </Typography>
                        )}

                        <Stack direction="row" spacing={0.5} alignItems="center" mt={1.5}>
                          <UpdateRounded sx={{ fontSize: 13, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.disabled">
                            {t.lastSyncedAt ? `Synced ${new Date(t.lastSyncedAt).toLocaleString()}` : 'Not yet synced'}
                          </Typography>
                        </Stack>
                      </CardContent>
                      <Divider />
                      <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<VisibilityOutlined fontSize="small" />}
                          onClick={() => openPreview(t)}
                          sx={{ color: 'text.secondary', fontWeight: 500, '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}
                        >
                          Preview
                        </Button>
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          {t.status === 'DRAFT' && (
                            <>
                              <Tooltip title="Edit draft">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(newTemplatePath, { state: { draft: t } })}
                                  sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<SendOutlined fontSize="small" />}
                                disabled={submittingDraft === `${t.name}::${t.language}`}
                                onClick={guard('whatsappEnabled', () => handleSubmitDraft(t))}
                              >
                                Submit
                              </Button>
                            </>
                          )}
                          {/* In-place edit — only Meta's editable statuses. PENDING is
                              mid-review (not editable); DRAFT uses the draft edit above. */}
                          {['APPROVED', 'REJECTED', 'PAUSED'].includes(t.status) && (
                            <Tooltip title="Edit template (re-submits to Meta for review)">
                              <IconButton
                                size="small"
                                onClick={guard('whatsappEnabled', () =>
                                  navigate(newTemplatePath, { state: { editTemplate: t } })
                                )}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Status details & history">
                            <IconButton
                              size="small"
                              onClick={() => setInfoTarget(t)}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {hasVariables && (
                            <Tooltip title="Label variables (used to auto-fill campaign mapping)">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const count = countBodyVariables(t.components?.body?.text || '');
                                  const existing = t.components?.body?.variableLabels || [];
                                  setLabelsValues(
                                    Array.from({ length: count }, (_, i) => existing[i] || '')
                                  );
                                  setHeaderLabelValue(t.components?.header?.headerVariableLabel || '');
                                  setLabelsTarget(t);
                                }}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
                              >
                                <LocalOfferOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={guard('whatsappEnabled', () => setDeleteTarget(t.name))}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'rgba(239,68,68,0.08)' } }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
          {!loading && filteredTemplates.length > 0 && (
            <TablePagination
              component="div"
              count={filteredTemplates.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </CardContent>
      </Card>

      {/* Variable labels editor */}
      <Dialog open={!!labelsTarget} onClose={() => setLabelsTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Label variables — {labelsTarget?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe what each variable represents (e.g. "Customer name", "Order ID", "Tracking
            link"). This is only used to auto-suggest the mapping when this template is selected
            in Create Campaign — it isn't sent to Meta.
          </Typography>

          {labelsTarget?.components?.header?.type === 'TEXT' &&
            /\{\{\s*1\s*\}\}/.test(labelsTarget.components.header.text || '') && (
              <TextField
                fullWidth
                size="small"
                label="Header {{1}}"
                placeholder="e.g. Customer name"
                value={headerLabelValue}
                onChange={(e) => setHeaderLabelValue(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}

          <Stack spacing={1.5}>
            {labelsValues.map((value, i) => (
              <TextField
                key={i}
                fullWidth
                size="small"
                label={`Body {{${i + 1}}}`}
                placeholder="e.g. Order ID"
                value={value}
                onChange={(e) => {
                  const next = [...labelsValues];
                  next[i] = e.target.value;
                  setLabelsValues(next);
                }}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelsTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={guard('whatsappEnabled', handleSaveVariableLabels)} disabled={labelsSaving}>
            {labelsSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status details & history */}
      <Dialog open={!!infoTarget} onClose={() => setInfoTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Status & history — {infoTarget?.name}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Current status:</Typography>
            {infoTarget && <StatusIndicator status={infoTarget.status} />}
          </Stack>

          {infoTarget?.rejectedReason && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {infoTarget.rejectedReason}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            History
          </Typography>

          {!infoTarget?.statusHistory?.length ? (
            <Typography variant="body2" color="text.secondary">
              No history recorded yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {[...infoTarget.statusHistory]
                .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
                .map((event, i) => (
                  <Box key={i} sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <StatusIndicator status={event.status} />
                      <Typography variant="caption" color="text.secondary">
                        {getSourceLabel(event.source)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(event.occurredAt).toLocaleString()}
                    </Typography>
                    {event.reason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {event.reason}
                      </Typography>
                    )}
                  </Box>
                ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will remove "{deleteTarget}" from Meta and from your local templates. This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={guard('whatsappEnabled', handleDelete)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template preview */}
      <Dialog open={!!previewTarget} onClose={() => setPreviewTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{previewTarget?.name}</DialogTitle>
        <DialogContent>
          {previewTarget && (() => {
            const header = previewTarget.components?.header;
            const isMediaHeader = header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type);
            const body = previewTarget.components?.body;
            const cards = previewTarget.components?.carousel?.cards;
            return (
              <>
                {header?.type === 'TEXT' && header.text && (
                  <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    Header: "{renderPreviewText(header.text, header.example?.header_text)}"
                  </Alert>
                )}
                <WhatsAppPreviewBubble
                  mediaUrl={isMediaHeader ? (previewMedia?.previewUrl || header.headerMediaUrl || null) : null}
                  mediaType={isMediaHeader ? (header!.type as 'IMAGE' | 'VIDEO' | 'DOCUMENT') : undefined}
                  mediaFileName={previewMedia?.fileName}
                  mediaLoading={isMediaHeader && previewMediaLoading}
                  bodyText={renderPreviewText(body?.text || '', body?.example?.body_text?.[0])}
                  footerText={previewTarget.components?.footer?.text}
                  buttons={previewTarget.components?.buttons}
                  carousel={
                    cards && cards.length > 0
                      ? cards.map((card, i) => ({
                          mediaUrl: previewCarouselMedia[i]?.previewUrl ?? card.header?.headerMediaUrl ?? null,
                          mediaType: card.header?.type,
                          mediaFileName: previewCarouselMedia[i]?.fileName,
                          mediaLoading: previewCarouselMediaLoading,
                          bodyText: renderPreviewText(card.body?.text || '', card.body?.example?.body_text?.[0]),
                          buttons: card.buttons,
                        }))
                      : undefined
                  }
                />
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppTemplates;
